/**
 * Что сделать со входом в лобби промо-матча против ИИ (REST /join и сокет
 * лобби). Чистое решение вынесено из server/index.js ради тестов: сервер
 * только применяет его — сажает игроком, пускает зрителем или отказывает.
 *
 * Обычное лобби верит присланному playerId, а id игроков публичны (список
 * лобби). В ИИ-лобби так нельзя: это прод-матч с рейтингом, и агент-ИИ —
 * участник, за которого можно было бы ходить. Поэтому:
 *  - за ИИ входит только владелец сервисного токена с ролью ai_agent;
 *  - человек входит только с настоящим входом (JWT-кука), и id из входа
 *    должен совпасть с присланным;
 *  - кто уже в лобби — возвращается на свое место (игроком или зрителем);
 *  - после старта матча или фазы банов, а также в полное лобби — только
 *    зрителем: состав уже поделен, а смотреть матч зовем всех.
 */

export const AI_PROMO_JOIN_KINDS = {
  PLAYER: 'PLAYER',
  SPECTATOR: 'SPECTATOR',
  REJECT: 'REJECT',
};

export const AI_PROMO_JOIN_REASONS = {
  // Сам ИИ по сервисному токену
  AI_AGENT: 'AI_AGENT',
  // Попытка войти за ИИ без роли ai_agent
  AI_IMPERSONATION: 'AI_IMPERSONATION',
  // Гость или нет действующего входа
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  // Вход есть, но под другим игроком, чем присланный playerId
  AUTH_MISMATCH: 'AUTH_MISMATCH',
  // Уже в лобби — на свое прежнее место
  REJOIN: 'REJOIN',
  MATCH_STARTED: 'MATCH_STARTED',
  // Началась фаза банов / выбора фракций: состав зафиксирован
  PHASE_STARTED: 'PHASE_STARTED',
  LOBBY_FULL: 'LOBBY_FULL',
  // Новый игрок на свободное место
  SEAT_AVAILABLE: 'SEAT_AVAILABLE',
};

export const AI_PROMO_AGENT_SERVICE_ROLE = 'ai_agent';

// ИИ и пять людей
export const AI_PROMO_MAX_PLAYERS = 6;

const plan = (kind, reason) => ({kind, reason});

// id приходят и строками, и ObjectId из базы — сравниваем по строке; пустой
// id не равен ничему (иначе незаданный AI_PROMO_USER_ID совпал бы с
// незаданным playerId)
const checkIfSameId = (a, b) => a != null && b != null && a !== '' && String(a) === String(b);

const checkIfInList = (list, playerId) => (list || []).some((entry) => checkIfSameId(entry?.id, playerId));

/**
 * @param {Object} params
 * @param {Object} params.lobby - лобби дня (players, spectators, status, поля фазы банов)
 * @param {string} params.playerId - присланный id
 * @param {string} params.aiUserId - id аккаунта ИИ (AI_PROMO_USER_ID)
 * @param {string|null} params.serviceRole - роль по сервисному токену или null
 * @param {string|null} params.jwtUserId - id из JWT-куки или null
 * @param {boolean} params.isGuest - гостевой аккаунт
 * @param {number} [params.maxPlayers]
 * @returns {{kind: string, reason: string}}
 */
export const planAiPromoJoin = ({
  lobby,
  playerId,
  aiUserId,
  serviceRole,
  jwtUserId,
  isGuest,
  maxPlayers = AI_PROMO_MAX_PLAYERS,
}) => {
  const {PLAYER, SPECTATOR, REJECT} = AI_PROMO_JOIN_KINDS;
  const reasons = AI_PROMO_JOIN_REASONS;

  if (checkIfSameId(playerId, aiUserId)) {
    return serviceRole === AI_PROMO_AGENT_SERVICE_ROLE
      ? plan(PLAYER, reasons.AI_AGENT)
      : plan(REJECT, reasons.AI_IMPERSONATION);
  }
  if (isGuest || !jwtUserId) {
    return plan(REJECT, reasons.AUTH_REQUIRED);
  }
  if (!checkIfSameId(jwtUserId, playerId)) {
    return plan(REJECT, reasons.AUTH_MISMATCH);
  }

  if (checkIfInList(lobby?.players, playerId)) {
    return plan(PLAYER, reasons.REJOIN);
  }
  if (checkIfInList(lobby?.spectators, playerId)) {
    return plan(SPECTATOR, reasons.REJOIN);
  }

  // Сыгранный матч ('finished' — окно завершения, лобби еще в памяти) —
  // тоже не место для нового игрока, даже если метки фазы банов сброшены
  if (lobby?.status === 'game_started' || lobby?.status === 'finished') {
    return plan(SPECTATOR, reasons.MATCH_STARTED);
  }
  if (lobby?.factionBanDeadlineAt || lobby?.isFactionBanPhaseResolved) {
    return plan(SPECTATOR, reasons.PHASE_STARTED);
  }
  if ((lobby?.players || []).length >= maxPlayers) {
    return plan(SPECTATOR, reasons.LOBBY_FULL);
  }
  return plan(PLAYER, reasons.SEAT_AVAILABLE);
};
